import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  EnvelopeSimple, ChatCircleDots, Briefcase, Trash, Eye, EyeSlash,
  Plus, FloppyDisk,
} from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function NewsletterPanel() {
  const [data, setData] = useState({ subscribers: [], total: 0, active: 0 });
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    const r = await axios.get(`${API}/admin/newsletter/subscribers`, { withCredentials: true });
    setData(r.data);
    setLoading(false);
  }, []);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const remove = async (id) => {
    await axios.delete(`${API}/admin/newsletter/subscribers/${id}`, { withCredentials: true });
    fetchAll();
  };

  const csv = "email,name,is_active,created_at\n" + data.subscribers.map(s =>
    `${s.email},${(s.name || "").replace(/,/g, " ")},${s.is_active !== false},${s.created_at}`
  ).join("\n");
  const csvUrl = `data:text/csv;charset=utf-8,${encodeURIComponent(csv)}`;

  return (
    <div className="space-y-3" data-testid="newsletter-panel">
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-stone-900 rounded-xl border border-stone-800 p-4">
          <p className="text-[10px] text-stone-500 uppercase tracking-wider">Total</p>
          <p className="text-2xl font-heading font-bold text-white">{data.total}</p>
        </div>
        <div className="bg-stone-900 rounded-xl border border-stone-800 p-4">
          <p className="text-[10px] text-stone-500 uppercase tracking-wider">Active</p>
          <p className="text-2xl font-heading font-bold text-emerald-400">{data.active}</p>
        </div>
        <a href={csvUrl} download="newsletter_subscribers.csv"
           className="bg-stone-900 rounded-xl border border-stone-800 p-4 hover:border-emerald-700/40 transition-colors flex flex-col justify-center"
           data-testid="download-subscribers">
          <p className="text-[10px] text-stone-500 uppercase tracking-wider">Export</p>
          <p className="text-sm text-emerald-400 mt-1">Download CSV</p>
        </a>
      </div>
      {loading ? <p className="text-stone-500 text-sm">Loading…</p> : (
        <div className="bg-stone-900 rounded-xl border border-stone-800 overflow-hidden">
          {data.subscribers.length === 0 ? (
            <p className="text-stone-500 text-sm text-center py-8">No subscribers yet.</p>
          ) : data.subscribers.map((s) => (
            <div key={s.id} className="flex items-center gap-3 px-4 py-2.5 border-b border-stone-800/40 last:border-0 text-sm"
                 data-testid={`sub-${s.id}`}>
              <EnvelopeSimple size={14} className="text-stone-500 shrink-0" />
              <span className="flex-1 truncate">{s.email}</span>
              {s.name && <span className="text-xs text-stone-500 truncate">{s.name}</span>}
              <span className="text-[10px] text-stone-600">{new Date(s.created_at).toLocaleDateString()}</span>
              <button onClick={() => remove(s.id)}
                className="text-xs text-red-400 hover:text-red-300"
                data-testid={`del-sub-${s.id}`}>
                <Trash size={12} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ContactPanel() {
  const [data, setData] = useState({ messages: [], unread: 0, total: 0 });
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    const r = await axios.get(`${API}/admin/contact`, { withCredentials: true });
    setData(r.data);
    setLoading(false);
  }, []);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const markRead = async (id) => {
    await axios.put(`${API}/admin/contact/${id}/read`, {}, { withCredentials: true });
    fetchAll();
  };
  const remove = async (id) => {
    await axios.delete(`${API}/admin/contact/${id}`, { withCredentials: true });
    fetchAll();
  };

  return (
    <div className="space-y-3" data-testid="contact-panel">
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-stone-900 rounded-xl border border-stone-800 p-4">
          <p className="text-[10px] text-stone-500 uppercase tracking-wider">Total messages</p>
          <p className="text-2xl font-heading font-bold text-white">{data.total}</p>
        </div>
        <div className="bg-stone-900 rounded-xl border border-stone-800 p-4">
          <p className="text-[10px] text-stone-500 uppercase tracking-wider">Unread</p>
          <p className="text-2xl font-heading font-bold text-amber-400">{data.unread}</p>
        </div>
      </div>
      {loading ? <p className="text-stone-500 text-sm">Loading…</p> : (
        <div className="space-y-2">
          {data.messages.length === 0 ? (
            <p className="text-stone-500 text-sm text-center py-8">No messages yet.</p>
          ) : data.messages.map((m) => (
            <div key={m.id} className={`bg-stone-900 rounded-xl border p-4 ${m.is_read ? "border-stone-800" : "border-amber-700/40"}`}
                 data-testid={`msg-${m.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-heading text-sm font-semibold text-white">{m.name}</p>
                    <a href={`mailto:${m.email}`} className="text-xs text-emerald-400 hover:text-emerald-300">{m.email}</a>
                    {!m.is_read && <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-600/20 text-amber-300 font-bold uppercase tracking-wider">unread</span>}
                  </div>
                  <p className="text-xs text-stone-500 mt-0.5">{m.subject} · {new Date(m.created_at).toLocaleString()}</p>
                  <p className="text-sm text-stone-300 mt-2 whitespace-pre-line">{m.message}</p>
                </div>
                <div className="flex flex-col gap-1 shrink-0">
                  {!m.is_read && (
                    <button onClick={() => markRead(m.id)}
                      className="bg-emerald-600/20 text-emerald-300 text-[11px] px-2.5 py-1 rounded-lg inline-flex items-center gap-1"
                      data-testid={`mark-read-${m.id}`}>
                      <Eye size={10} /> Mark read
                    </button>
                  )}
                  <button onClick={() => window.confirm("Delete this message?") && remove(m.id)}
                    className="bg-red-600/20 text-red-300 text-[11px] px-2.5 py-1 rounded-lg inline-flex items-center gap-1"
                    data-testid={`del-msg-${m.id}`}>
                    <Trash size={10} /> Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CareersPanel() {
  const [jobs, setJobs] = useState([]);
  const [adding, setAdding] = useState(false);
  const [newJ, setNewJ] = useState({ title: "", team: "", location: "Remote", employment_type: "Full-time", description: "", requirements: "", apply_url: "", is_active: true });

  const fetchAll = useCallback(async () => {
    const r = await axios.get(`${API}/admin/careers`, { withCredentials: true });
    setJobs(r.data?.jobs || []);
  }, []);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const create = async () => {
    if (!newJ.title.trim()) return;
    await axios.post(`${API}/admin/careers`, newJ, { withCredentials: true });
    setNewJ({ title: "", team: "", location: "Remote", employment_type: "Full-time", description: "", requirements: "", apply_url: "", is_active: true });
    setAdding(false);
    fetchAll();
  };
  const toggle = async (j) => {
    await axios.put(`${API}/admin/careers/${j.id}`, { is_active: !j.is_active }, { withCredentials: true });
    fetchAll();
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this job?")) return;
    await axios.delete(`${API}/admin/careers/${id}`, { withCredentials: true });
    fetchAll();
  };

  return (
    <div className="space-y-3" data-testid="careers-panel">
      <div className="flex items-center justify-between">
        <p className="text-sm text-stone-400">{jobs.length} total · {jobs.filter(j => j.is_active).length} active</p>
        <button onClick={() => setAdding(!adding)}
          className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs px-3 py-2 rounded-lg inline-flex items-center gap-1.5"
          data-testid="add-job-btn">
          <Plus size={14} /> {adding ? "Cancel" : "Add Role"}
        </button>
      </div>
      {adding && (
        <div className="bg-stone-900 rounded-xl border border-emerald-700/30 p-4 space-y-2">
          <input value={newJ.title} onChange={(e) => setNewJ({ ...newJ, title: e.target.value })}
            placeholder="Role title (e.g. Senior Data Engineer)"
            data-testid="new-job-title"
            className="w-full bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-emerald-500" />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <input value={newJ.team} onChange={(e) => setNewJ({ ...newJ, team: e.target.value })}
              placeholder="Team" data-testid="new-job-team"
              className="bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-xs text-white focus:outline-none focus:border-emerald-500" />
            <input value={newJ.location} onChange={(e) => setNewJ({ ...newJ, location: e.target.value })}
              placeholder="Location"
              className="bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-xs text-white focus:outline-none focus:border-emerald-500" />
            <input value={newJ.employment_type} onChange={(e) => setNewJ({ ...newJ, employment_type: e.target.value })}
              placeholder="Employment type"
              className="bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-xs text-white focus:outline-none focus:border-emerald-500" />
          </div>
          <textarea value={newJ.description} onChange={(e) => setNewJ({ ...newJ, description: e.target.value })}
            placeholder="Description" rows={3} data-testid="new-job-desc"
            className="w-full bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-xs text-stone-100 focus:outline-none focus:border-emerald-500" />
          <textarea value={newJ.requirements} onChange={(e) => setNewJ({ ...newJ, requirements: e.target.value })}
            placeholder="Requirements (one per line)" rows={3}
            className="w-full bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-xs text-stone-100 focus:outline-none focus:border-emerald-500" />
          <input value={newJ.apply_url} onChange={(e) => setNewJ({ ...newJ, apply_url: e.target.value })}
            placeholder="Apply URL (e.g. mailto:hiring@… or Greenhouse link)"
            className="w-full bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-xs text-white font-mono focus:outline-none focus:border-emerald-500" />
          <div className="flex justify-end">
            <button onClick={create}
              className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs px-4 py-2 rounded-lg inline-flex items-center gap-1"
              data-testid="create-job-submit">
              <FloppyDisk size={12} /> Post role
            </button>
          </div>
        </div>
      )}
      <div className="space-y-2">
        {jobs.length === 0 && <p className="text-stone-500 text-sm text-center py-6">No roles yet.</p>}
        {jobs.map((j) => (
          <div key={j.id} className="bg-stone-900 rounded-xl border border-stone-800 p-4 flex items-start gap-3"
               data-testid={`admin-job-${j.id}`}>
            <Briefcase size={18} weight="duotone" className="text-emerald-400 shrink-0 mt-1" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-heading text-sm font-semibold text-white">{j.title}</p>
                {!j.is_active && <span className="text-[9px] px-1.5 py-0.5 rounded bg-stone-700 text-stone-400 font-bold uppercase">Hidden</span>}
              </div>
              <p className="text-[11px] text-stone-500 mt-0.5">{[j.team, j.location, j.employment_type].filter(Boolean).join(" · ")}</p>
              {j.description && <p className="text-xs text-stone-400 mt-2 line-clamp-2">{j.description}</p>}
            </div>
            <div className="flex flex-col gap-1 shrink-0">
              <button onClick={() => toggle(j)}
                className={`text-xs px-3 py-1.5 rounded-lg inline-flex items-center gap-1 ${j.is_active ? "bg-amber-600/20 text-amber-300" : "bg-emerald-600/20 text-emerald-300"}`}
                data-testid={`toggle-job-${j.id}`}>
                {j.is_active ? <><EyeSlash size={11} /> Hide</> : <><Eye size={11} /> Show</>}
              </button>
              <button onClick={() => remove(j.id)}
                className="bg-red-600/20 text-red-300 text-xs px-3 py-1.5 rounded-lg inline-flex items-center gap-1"
                data-testid={`del-job-${j.id}`}>
                <Trash size={11} /> Delete
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function InboxManager() {
  const [subTab, setSubTab] = useState("newsletter");
  return (
    <div className="space-y-4" data-testid="inbox-manager">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-emerald-600/20 flex items-center justify-center shrink-0">
          <ChatCircleDots size={18} weight="duotone" className="text-emerald-400" />
        </div>
        <div>
          <h2 className="font-heading text-lg font-semibold text-white">Inbox & Careers</h2>
          <p className="text-xs text-stone-400 mt-0.5">Newsletter subscribers, contact-form messages, and job postings.</p>
        </div>
      </div>

      <div className="flex gap-1 border-b border-stone-800">
        {[
          { id: "newsletter", label: "Newsletter", Icon: EnvelopeSimple },
          { id: "contact", label: "Contact", Icon: ChatCircleDots },
          { id: "careers", label: "Careers", Icon: Briefcase },
        ].map((t) => (
          <button key={t.id} onClick={() => setSubTab(t.id)}
            className={`px-4 py-2 text-xs font-medium border-b-2 -mb-px inline-flex items-center gap-1.5 ${subTab === t.id ? "border-emerald-400 text-emerald-300" : "border-transparent text-stone-500 hover:text-stone-300"}`}
            data-testid={`inbox-tab-${t.id}`}>
            <t.Icon size={13} /> {t.label}
          </button>
        ))}
      </div>

      {subTab === "newsletter" && <NewsletterPanel />}
      {subTab === "contact" && <ContactPanel />}
      {subTab === "careers" && <CareersPanel />}
    </div>
  );
}
